import React, { useMemo, useState, useEffect, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  Typography,
  Button,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  saveSchedule,
  fetchUserSchedules,
  fetchControllerSchedules,
} from 'stores/sensor/controllersSlice';
import { useParams } from 'react-router-dom';
import CustomSnackbar from 'components/CustomSnackbar';
import { getSchedule } from './schedulesCache.js';
import Loading from 'layouts/ControllerPage/components/Loading';
import CircularProgress from '@mui/material/CircularProgress';
import { LOCK_LABELS, normalizeLockValue } from '../../constants/lockOptions.js';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0..23

// Fixed hex palette for UI chrome (no theme/system colors)
const HEX = {
  white: '#FFFFFF',
  gridBorder: '#bcbcbcff',
  hoverOutline: '#585858ff',
  hourBg: '#F5F5F5',
  dayBg: '#FAFAFA',
  text: '#111111ff',
  subText: '#484848ff',
};

const DEFAULT_MODE = 'auto';
const DEFAULT_FAN_SPEED = 'auto';

const DEFAULT_ON_COLOR = '#33c83dff';
const HEX_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const isValidHex = value => typeof value === 'string' && HEX_REGEX.test(value);

const DEFAULT_PATTERN = {
  on_off: 0,
  device_type: null,
  smart_mode: false,
  comfort_mode: 'Neutral',
  channel_1: 0,
  channel_2: 0,
  mode: DEFAULT_MODE,
  fan_speed: DEFAULT_FAN_SPEED,
  temp_set_point: 25,
  lock: 0,
  color: '#ffffff',
  preset_id: null,
};

const normalizeOnOff = value => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'on') return 1;
    if (lower === 'off') return 0;
  }
  if (value === true) return 1;
  if (value === false) return 0;
  return Number(value) === 1 ? 1 : 0;
};

const normalizeMode = value => {
  const key = String(value ?? '').trim().toLowerCase().replace(/[ -]/g, '_');
  return ['fan', 'cool', 'heat', 'auto'].includes(key) ? key : DEFAULT_MODE;
};

const normalizeFanSpeed = value => {
  const key = String(value ?? '').trim().toLowerCase();
  return ['auto', 'low', 'medium', 'high'].includes(key) ? key : DEFAULT_FAN_SPEED;
};

const normalizeTemp = value => {
  const num = Number(value);
  return Number.isFinite(num) ? num : DEFAULT_PATTERN.temp_set_point;
};

const resolveColor = (color, onOff) => {
  if (isValidHex(color)) return color;
  return onOff === 1 ? DEFAULT_ON_COLOR : null;
};

const getContrastTextColor = color => {
  if (!isValidHex(color)) return '#fff';
  const hex = color.replace('#', '');
  const fullHex =
    hex.length === 3
      ? hex
          .split('')
          .map(c => c + c)
          .join('')
      : hex;
  const r = parseInt(fullHex.slice(0, 2), 16);
  const g = parseInt(fullHex.slice(2, 4), 16);
  const b = parseInt(fullHex.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 186 ? '#000' : '#fff';
};

const parseScheduleTimeToMinutes = value => {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const [hours, minutes = '0'] = value.split(':');
  const parsedHours = Number(hours);
  const parsedMinutes = Number(minutes);

  if (
    !Number.isInteger(parsedHours) ||
    !Number.isInteger(parsedMinutes) ||
    parsedHours < 0 ||
    parsedHours > 23 ||
    parsedMinutes < 0 ||
    parsedMinutes > 59
  ) {
    return null;
  }

  return parsedHours * 60 + parsedMinutes;
};

const isBuildingHourInsideCavRange = (dayIdx, hourIdx, building) => {
  const cavStartTime = building?.cav_start_time;
  const cavEndTime = building?.cav_end_time;
  const startMinutes = parseScheduleTimeToMinutes(cavStartTime?.[String(dayIdx)]);
  const endMinutes = parseScheduleTimeToMinutes(cavEndTime?.[String(dayIdx)]);

  if (startMinutes === null || endMinutes === null) return false;

  const hourStartMinutes = hourIdx * 60;
  if (endMinutes <= startMinutes) {
    return hourStartMinutes >= startMinutes || hourStartMinutes < endMinutes;
  }

  return hourStartMinutes >= startMinutes && hourStartMinutes < endMinutes;
};

const isHourInsideCavRange = (dayIdx, hourIdx, buildings) => {
  if (!Array.isArray(buildings) || buildings.length === 0) return true;

  return buildings.every(building => isBuildingHourInsideCavRange(dayIdx, hourIdx, building));
};

const hydrateCell = data => {
  if (!data) return null;
  const on_off = normalizeOnOff(data.on_off ?? data.power);
  // const lock = normalizeLockValue(lock ?? data.lock);
  return {
    on_off,
    device_type: data.device_type ?? DEFAULT_PATTERN.device_type,
    mode: normalizeMode(data.mode),
    smart_mode: data.smart_mode ?? DEFAULT_PATTERN.smart_mode,
    comfort_mode: data.comfort_mode ?? DEFAULT_PATTERN.comfort_mode,
    channel_1: normalizeOnOff(data.channel_1),
    channel_2: normalizeOnOff(data.channel_2),
    fan_speed: normalizeFanSpeed(data.fan_speed ?? data.fan),
    temp_set_point: normalizeTemp(data.temp_set_point ?? data.temp),
    color: resolveColor(data.color, on_off),
    lock: data.lock,
    preset_id: data.preset_id ?? data.presetId ?? data.id ?? null,
  };
};

const getCellColorValue = cell => (typeof cell === 'string' ? cell : (cell?.color ?? null));
const isColoredCell = color => {
  if (!color) return false;
  const normalizedColor = color.toLowerCase();
  const normalizedWhite = (HEX.white || '#ffffff').toLowerCase();
  return normalizedColor !== normalizedWhite;
};
const createDefaultCell = () => ({ ...DEFAULT_PATTERN });

// Show the wand on smart-enabled pattern cells instead of the schedule button.
const isSmartPatternEnabled = value => {
  if (value === true || value === 1) return true;
  const smartMode = String(value ?? '').toLowerCase();
  return smartMode === 'true' || smartMode === 'solai' || smartMode === '1';
};

const cellHasSmartMode = cell => cell && typeof cell === 'object' && isSmartPatternEnabled(cell.smart_mode);

const SmartPatternIcon = ({ color }) => (
  <AutoFixHighIcon
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 20,
      height: 20,
      color,
      pointerEvents: 'none',
      filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.45))',
    }}
  />
);

// 7x24 matrix of null | cell objects
const makeEmptyGrid = () => Array.from({ length: 7 }, () => Array(24).fill(null));
const makeDefaultGrid = () =>
  Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => createDefaultCell()));

const coerceCell = cell => {
  if (cell && typeof cell === 'object') return cell;
  if (typeof cell === 'string') {
    return {
      on_off: DEFAULT_PATTERN.on_off,
      device_type: DEFAULT_PATTERN.device_type,
      smart_mode: DEFAULT_PATTERN.smart_mode,
      comfort_mode: DEFAULT_PATTERN.comfort_mode,
      channel_1: DEFAULT_PATTERN.channel_1,
      channel_2: DEFAULT_PATTERN.channel_2,
      mode: DEFAULT_PATTERN.mode,
      fan_speed: DEFAULT_PATTERN.fan_speed,
      temp_set_point: DEFAULT_PATTERN.temp_set_point,
      color: resolveColor(cell, DEFAULT_PATTERN.on_off),
      lock: DEFAULT_PATTERN.lock,
      preset_id: null,
    };
  }
  return { ...DEFAULT_PATTERN };
};

const cellToPattern = (cell, dayIdx, hourIdx) => {
  const base = coerceCell(cell);
  const on_off = base.on_off ?? DEFAULT_PATTERN.on_off;

  return {
    day_of_week: dayIdx,
    hour_of_day: hourIdx,
    on_off,
    device_type: base.device_type ?? DEFAULT_PATTERN.device_type,
    smart_mode: base.smart_mode ?? DEFAULT_PATTERN.smart_mode,
    comfort_mode: base.comfort_mode ?? DEFAULT_PATTERN.comfort_mode,
    channel_1: base.channel_1 ?? DEFAULT_PATTERN.channel_1,
    channel_2: base.channel_2 ?? DEFAULT_PATTERN.channel_2,
    mode: base.mode ?? DEFAULT_PATTERN.mode,
    fan_speed: base.fan_speed ?? DEFAULT_PATTERN.fan_speed,
    temp_set_point: base.temp_set_point ?? DEFAULT_PATTERN.temp_set_point,
    color: base.color ?? (on_off === 1 ? DEFAULT_ON_COLOR : null),
    lock: base.lock,
    preset_id: base.preset_id ?? null,
  };
};

const prepareInitialGrid = initialGrid => {
  if (!Array.isArray(initialGrid) || initialGrid.length !== 7) return makeEmptyGrid();
  return initialGrid.map(row => {
    if (!Array.isArray(row)) return Array(24).fill(null);
    return Array.from({ length: 24 }, (_, hourIdx) => {
      const cell = row[hourIdx];
      if (!cell) return null;
      if (typeof cell === 'object') return hydrateCell(cell);
      if (typeof cell === 'string') return hydrateCell({ color: cell });
      return null;
    });
  });
};

const patternsToGrid = (patterns = []) => {
  const next = makeEmptyGrid();

  for (const p of patterns) {
    // If your API is 0=Sun..6=Sat this is fine.
    // If it’s 1=Mon..7=Sun, uncomment the next line instead:
    // const d = ((Number(p.day_of_week) + 6) % 7); // shift so Mon(1) -> 1, Sun(7) -> 0
    const d = Number(p.day_of_week) ?? 0;
    const h = Number(p.hour_of_day) ?? 0;

    if (d >= 0 && d < 7 && h >= 0 && h < 24) {
      next[d][h] = hydrateCell(p, p.lock);
    }
  }
  return next;
};

// Optional: pass `initialGrid` and `onGridChange` from parent if you want external control
const ScheduleTable168 = React.forwardRef(function ScheduleTable168(
  { initialGrid, onGridChange, removeMode = false, showRelayPresets = false, onUserEdit },
  ref,
) {
  const scheduleId = Number(useParams().scheduleId);
  // const deviceId = useParams().id;

  const dispatch = useDispatch();
  const schedule = getSchedule(scheduleId);
  const selectedPreset = useSelector(s => s.controllers?.selectedSinglePreset);
  const presets = useSelector(s => s.controllers?.hourlyPresetsArray || []);
  const scheduleBuildings = schedule?.buildings || [];
  const isPresetMissing = !selectedPreset;

  const [scheduleName, setScheduleName] = useState(schedule?.schedule_name || '');

  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [clearConfirmPending, setClearConfirmPending] = useState(false);
  const [grid, setGrid] = useState(() => {
    if (initialGrid) return prepareInitialGrid(initialGrid);
    if (Array.isArray(schedule?.patterns)) return patternsToGrid(schedule.patterns);
    return makeEmptyGrid();
  });

  const patterns = useMemo(() => {
    return grid.flatMap((row, dayIdx) =>
      row.map((cell, hourIdx) => cellToPattern(cell, dayIdx, hourIdx)),
    );
  }, [grid]);

  const presetNameById = useMemo(() => {
    const map = new Map();
    presets.forEach(p => {
      if (p && p.id != null && typeof p.name === 'string' && p.name.trim() !== '') {
        map.set(String(p.id), p.name);
      }
    });
    return map;
  }, [presets]);

  const presetById = useMemo(() => {
    const map = new Map();
    presets.forEach(p => {
      if (p && p.id != null) {
        map.set(String(p.id), p);
      }
    });
    return map;
  }, [presets]);

  const resolvePresetNameForCell = p => {
    if (!p) return null;

    if (p.preset_id != null) {
      const byId = presetNameById.get(String(p.preset_id));
      if (byId) return byId;
    }

    const normalizeMode = value => {
      const label = String(value ?? '').trim().toLowerCase().replace(/[ -]/g, '_');
      return {
        fanonly: 'fan',
        fan_only: 'fan',
        vent: 'fan',
        ventilation: 'fan',
        cooling: 'cool',
        heating: 'heat',
        automatic: 'auto',
      }[label] ?? label;
    };
    const normalizeFan = value => {
      const label = String(value ?? '').trim().toLowerCase();
      return {
        automatic: 'auto',
        lo: 'low',
        med: 'medium',
        mid: 'medium',
        middle: 'medium',
        hi: 'high',
      }[label] ?? label;
    };
    const power = Number(p.on_off) === 1 ? 'on' : 'off';
    const mode = normalizeMode(p.mode);
    const fan = normalizeFan(p.fan_speed);
    const temp = Number(p.temp_set_point);
    const lock = Number(p.lock ?? 0);

    const matchedPreset = presets.find(sp => {
      if (!sp) return false;
      return (
        String(sp.power ?? '').toLowerCase() === power &&
        normalizeMode(sp.mode) === mode &&
        normalizeFan(sp.fan) === fan &&
        Number(sp.temp) === temp &&
        Number(sp.lock ?? 0) === lock
      );
    });

    return matchedPreset?.name || null;
  };

  // find the pattern object for a specific (dayIdx, hourIdx)
  const getPatternForCell = (dayIdx, hourIdx) => {
    // patterns is an array of 168 objects like:
    // { day_of_week:0, hour_of_day:0, fan_speed:'high', mode:'auto', on_off:0, temp_set_point:25 }
    return patterns.find(p => p.day_of_week === dayIdx && p.hour_of_day === hourIdx);
  };

  const getTooltipTitle = (dayIdx, hourIdx) => {
    const p = getPatternForCell(dayIdx, hourIdx);

    // fallback in case nothing set yet
    if (!p) {
      return `${DAYS[dayIdx]} ${hourIdx}:00
    off`;
    }

    const onOffLabel = p.on_off === 1 ? 'ON' : 'OFF';
    const modeLabel = String(p.mode ?? '').toUpperCase();
    const fanLabel = String(p.fan_speed ?? '').toUpperCase();
    const presetName = resolvePresetNameForCell(p);
    const preset = p.preset_id != null ? presetById.get(String(p.preset_id)) : null;
    const relaySource = preset || p;
    const relayModeLabel = String(relaySource.mode ?? modeLabel).toUpperCase();
    const relayControl = String(
      relaySource.smart_mode ?? relaySource.smart_mode ?? DEFAULT_PATTERN.smart_mode,
    ).toLowerCase();
    const smartModeEnabled = relayControl === 'true' || relayControl === 'solai';
    const relayLines = [`${DAYS[dayIdx]} ${hourIdx}:00 - ${hourIdx + 1}:00.`];

    relayLines.push(
      `Smart mode: ${smartModeEnabled ? 'ON' : 'OFF'}`,
      `Mode: ${relayModeLabel}`,
      `Comfort Mode: ${relaySource.comfort_mode ?? DEFAULT_PATTERN.comfort_mode}`,
    );

    if (relayControl === 'false' || relayControl === 'manual') {
      const relayPower = relaySource.on_off ?? relaySource.power ?? p.on_off;
      relayLines.push(`Power: ${normalizeOnOff(relayPower) === 1 ? 'ON' : 'OFF'}`);
    }

    const details = showRelayPresets
      ? relayLines.join('\n')
      : `${DAYS[dayIdx]} ${hourIdx}:00 - ${hourIdx + 1}:00.
    Smart mode: ${smartModeEnabled ? 'ON' : 'OFF'},${
      smartModeEnabled
        ? ''
        : `
    Power: ${onOffLabel},`
    }
    Temp: ${p.temp_set_point}°C,
    Mode: ${modeLabel},
    Fan: ${fanLabel},
    Lock: ${LOCK_LABELS[p.lock ?? 0] ?? (p.lock ? 'Locked' : 'Unlocked')}`.replace(/,\s*/g, ',\n');

    return (
      <Box>
        {presetName ? (
          <Typography component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {presetName}
          </Typography>
        ) : null}
        <Typography component="div" sx={{ whiteSpace: 'pre-line', fontSize: '1rem' }}>
          {details}
        </Typography>
      </Box>
    );
  };

  // Grab the preset from Redux: state.controllers.selectedSinglePreset
  // Expected shape: { id, name, color: '#RRGGBB', ... }
  // Keep only valid hex colors. If invalid/missing, disable painting.
  const activePresetCell = useMemo(() => {
    if (!selectedPreset) return null;
    if (!isValidHex(selectedPreset.color)) return null;
    return hydrateCell({
      on_off: selectedPreset.power === 'on' ? 1 : 0,
      device_type: selectedPreset.device_type ?? DEFAULT_PATTERN.device_type,
      smart_mode: selectedPreset.smart_mode ?? DEFAULT_PATTERN.smart_mode,
      comfort_mode: selectedPreset.comfort_mode ?? DEFAULT_PATTERN.comfort_mode,
      channel_1: selectedPreset.channel_1 ?? DEFAULT_PATTERN.channel_1,
      channel_2: selectedPreset.channel_2 ?? DEFAULT_PATTERN.channel_2,
      mode: selectedPreset.mode,
      fan_speed: selectedPreset.fan,
      temp_set_point: selectedPreset.temp,
      color: selectedPreset.color,
      preset_id: selectedPreset.id ?? null,
      lock: selectedPreset.lock,
    });
  }, [selectedPreset]);

  const cellsMatch = (currentCell, presetCell) => {
    if (!currentCell || !presetCell) return false;
    const currentPresetId =
      typeof currentCell === 'object' && currentCell !== null
        ? (currentCell.preset_id ?? null)
        : null;
    if (currentPresetId && presetCell.preset_id) {
      return currentPresetId === presetCell.preset_id;
    }
    return getCellColorValue(currentCell) === getCellColorValue(presetCell);
  };

  const apply = (dayIdx, hourIdx) => {
    if (!isHourInsideCavRange(dayIdx, hourIdx, scheduleBuildings)) return;

    const currentCell = grid?.[dayIdx]?.[hourIdx];
    const currentColor = getCellColorValue(currentCell);
    const colored = isColoredCell(currentColor);

    if (removeMode) {
      setGrid(prev => {
        const next = prev.map(row => row.slice());
        next[dayIdx][hourIdx] = createDefaultCell();
        onGridChange?.(next);
        return next;
      });
      onUserEdit?.();
      return;
    }

    if (!activePresetCell) {
      return;
    }

    setGrid(prev => {
      const next = prev.map(row => row.slice());
      const existing = next[dayIdx][hourIdx];
      // toggle: same preset/color → clear, otherwise set data
      next[dayIdx][hourIdx] = cellsMatch(existing, activePresetCell)
        ? null
        : { ...activePresetCell };
      onGridChange?.(next);
      return next;
    });
    onUserEdit?.();
  };

  const handleClearAll = () => {
    if (!clearConfirmPending) {
      setClearConfirmPending(true);
      setSnack({
        open: true,
        message: 'click again to confirm clear',
        severity: 'info',
      });
      return;
    }

    const next = makeDefaultGrid();
    setGrid(next);
    onGridChange?.(next);
    onUserEdit?.();
    setClearConfirmPending(false);
  };

  const cellSx = (color, colored, disabled) => ({
    position: 'relative',
    minWidth: 40,
    minHeight: 10,
    maxHeight: '3px',
    p: 0,
    border: `1px solid ${HEX.gridBorder}`,
    // no 'transparent': when empty, just omit backgroundColor
    ...(disabled
      ? { backgroundColor: '#3a3a3a', opacity: 0.38 }
      : color
        ? { backgroundColor: color }
        : { backgroundColor: HEX.white }),
    '&:hover': disabled ? {} : { outline: `1px dashed ${HEX.hoverOutline}` },
    cursor: disabled || removeMode ? 'not-allowed' : 'pointer',
  });

  const handleSaveSchedule = (triggerSource = 'table', payload) => {
    console.log(`handleSaveSchedule triggered by: ${triggerSource}`, payload || '');

    let scheduleData;

    dispatch(saveSchedule({ scheduleData, patterns }))
      .unwrap()
      .then(() => {
        setSnack({
          open: true,
          message: 'Saved Schedule',
          severity: 'success',
        });
        dispatch(fetchUserSchedules());
        // dispatch(fetchControllerSchedules());
      })
      .catch(() =>
        setSnack({
          open: true,
          message: 'Error saving Schedule',
          severity: 'error',
        }),
      );
  };

  useImperativeHandle(ref, () => ({
    saveSchedule: (trigger, payload) => handleSaveSchedule(trigger, payload),
    getPatterns: () => patterns,
  }));

  useEffect(() => {
    if (!clearConfirmPending) return undefined;
    const confirmTimeoutId = setTimeout(() => setClearConfirmPending(false), 4000);
    return () => clearTimeout(confirmTimeoutId);
  }, [clearConfirmPending]);

  useEffect(() => {
    if (!scheduleId || !schedule) {
      const empty = makeEmptyGrid();
      setGrid(empty);
      onGridChange?.(empty);
      return;
    }

    if (Array.isArray(schedule?.patterns)) {
      const next = patternsToGrid(schedule.patterns);
      setGrid(next);
      onGridChange?.(next);
      setScheduleName(schedule?.schedule_name || '');
      return;
    }

    if (initialGrid) {
      const next = prepareInitialGrid(initialGrid);
      setGrid(next);
      onGridChange?.(next);
    } else {
      const empty = makeEmptyGrid();
      setGrid(empty);
      onGridChange?.(empty);
    }
  }, [scheduleId, schedule?.patterns, schedule?.schedule_name, initialGrid, onGridChange]);

  if (!patterns) {
    return <CircularProgress />;
  }

  // console.log('ScheduleTable168 RERENDER!');
  // console.log('patterns:', patterns);

  return (
    <Box>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical={snack.message === 'click again to confirm clear' ? 'bottom' : 'top'}
        horizontal={snack.message === 'click again to confirm clear' ? 'right' : 'left'}
        color="#fff"
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Tooltip title="Grey cells represent hours when the building is inactive and AC schedules cannot be modified.">
          <InfoOutlinedIcon sx={{ color: HEX.subText, fontSize: 22, cursor: 'help' }} />
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClearAll}
          sx={{
            minWidth: 96,
            borderColor: clearConfirmPending ? '#d32f2f' : '#8a1f1f',
            color: clearConfirmPending ? '#ffffff' : '#8a1f1f',
            backgroundColor: clearConfirmPending ? '#d32f2f' : '#ffffff',
            fontWeight: 700,
            letterSpacing: 0,
            textTransform: 'uppercase',
            '&:hover': {
              borderColor: '#d32f2f',
              color: '#ffffff',
              backgroundColor: '#d32f2f',
            },
          }}>
          Clear
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ backgroundColor: HEX.white, borderRadius: '0' }}>
        <Table size="small">
          <TableBody>
            {DAYS.map((day, dIdx) => (
              <TableRow key={day}>
                {/* Day label on the LEFT */}
                <TableCell
                  sx={{
                    position: 'sticky', // keeps the label visible if container scrolls
                    left: 0,
                    zIndex: 1,
                    backgroundColor: HEX.dayBg,
                    border: `1px solid ${HEX.gridBorder}`,
                    width: 120,
                    fontWeight: 600,
                    color: HEX.text,
                    lineHeight: '.5',
                  }}>
                  {day}
                </TableCell>

                {/* 24 hour cells */}
                {HOURS.map(h => {
                  const cell = grid[dIdx][h];
                  const cellColor = getCellColorValue(cell);
                  const colored = isColoredCell(cellColor);
                  const isDisabled = !isHourInsideCavRange(dIdx, h, scheduleBuildings);
                  const tooltipBg = isValidHex(cellColor) ? cellColor : '#000';
                  const tooltipText = getContrastTextColor(tooltipBg);
                  const showSmartIcon = !isDisabled && cellHasSmartMode(cell);
                  return (
                    <Tooltip
                      key={h}
                      title={getTooltipTitle(dIdx, h)}
                      arrow
                      placement="top"
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: tooltipBg,
                            color: tooltipText,
                            boxShadow: '1px 3px 10px 0px rgba(0,0,0,0.75)',
                          },
                        },
                        arrow: { sx: { color: tooltipBg } },
                      }}>
                      <TableCell
                        onClick={() => apply(dIdx, h)}
                        sx={cellSx(cellColor, colored, isDisabled)}>
                        {showSmartIcon ? <SmartPatternIcon color={tooltipText} /> : null}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>

          {/* Hours row at the BOTTOM */}
          <TableFooter>
            <TableRow>
              {/* spacer cell under the day labels */}
              <TableCell
                sx={{
                  backgroundColor: HEX.hourBg,
                  borderTop: `1px solid ${HEX.gridBorder}`,
                  border: `1px solid ${HEX.gridBorder}`,
                  width: 120,
                  color: HEX.subText,
                  fontWeight: 600,
                  lineHeight: '1',
                }}>
                Hour
              </TableCell>
              {HOURS.map(h => (
                <TableCell
                  key={`h-${h}`}
                  align="left"
                  sx={{
                    backgroundColor: HEX.hourBg,
                    border: `1px solid ${HEX.gridBorder}`,
                    color: HEX.subText,
                    fontSize: 12,
                    fontWeight: '500',
                    padding: '2px 8px 2px 2px',
                  }}>
                  {h}:00
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
});

export default ScheduleTable168;
